import { groupBy, mapValues } from 'lodash'
import { ymd } from '@/utils/dateUtils'

const TEXT_SEPARATOR = '\n\n\n'

// Type definitions for the state objects
export interface ItineraryRequestState {
  purposes: string[]
  vibes: string[]
  otherVibe: string
  travelPace: string | null
  accommodations: string[]
  foodExperiences: string[]
  tripProfile: string
  vibeSummary: string
  groupTypes: string[]
  groupTypeOther: string
  group: string
  inspiration: string[]
  destType: string
  destCountries: string[]
  dates: {
    type: string
    specific: { startDate: any; endDate: any }
    duration: number
    durationUnit: string
    months: any[]
  }
  budget: string | null
  budgetArr: any[] | null
}

export interface ApiPayloadOptions {
  flowCompleted?: boolean
  source?: string
  tracking?: any
}

/**
 * Transform vibes data for API payload
 */
export function transformVibesForApi(state: ItineraryRequestState): { vibes: string[] } {
  return {
    vibes: [
      state.vibes,
      [state.otherVibe].filter(Boolean).map(s => `Other: ${s}`),
      [state.travelPace].filter(Boolean).map(s => `Pace: ${s}`),
      state.accommodations.filter(Boolean).map(s => `Accommodation: ${s}`),
      state.foodExperiences.filter(Boolean).map(s => `Food: ${s}`),
    ].flat(),
  }
}

/**
 * Transform country data for API payload
 */
export function transformCountryForApi(state: ItineraryRequestState): { country: string } {
  return {
    country: state.destType === 'flexible' ? 'all' : state.destCountries.join(','),
  }
}

/**
 * Transform dates data for API payload
 */
export function transformDatesForApi(state: ItineraryRequestState): { dates: any } {
  return {
    dates: {
      date_type: state.dates.type,
      specific_dates: [ymd(state.dates.specific.startDate), ymd(state.dates.specific.endDate)],
      duration: [state.dates.duration, state.dates.durationUnit].join(' '),
      flexible_dates: mapValues(groupBy(state.dates.months, 'year'), (year: any[]) => year.map(m => m.month)),
    },
  }
}

/**
 * Transform budget data for API payload
 */
export function transformBudgetForApi(state: ItineraryRequestState): any {
  const [min, max] = state.budgetArr ?? [null, null]

  return {
    budget: state.budget,
    minimum_budget_cents: min ? min * 100 : 0,
    maximum_budget_cents: max ? max * 100 : 0,
  }
}

/**
 * Create complete API payload for itinerary request
 */
export function createItineraryRequestApiPayload(
  state: ItineraryRequestState,
  options: ApiPayloadOptions = {},
): object {
  const { flowCompleted = false, source, tracking = {} } = options

  return {
    purposes: state.purposes,
    trip_profile: state.tripProfile,
    vibe_summary: state.vibeSummary,
    group: state.group,
    inspiration: state.inspiration.join(TEXT_SEPARATOR),
    flow_completed: flowCompleted,
    tracking,
    ...transformDatesForApi(state),
    ...transformCountryForApi(state),
    ...transformVibesForApi(state),
    ...transformBudgetForApi(state),
    ...(source && { source }),
  }
}
